import { __assign, __awaiter, __generator } from "tslib";
import { createDuplexerPubSub } from './duplexer-pubsub';
import { noop } from '@wix/answers-lib';
import * as uuid from 'uuid';
import { PubSubPresenceStatusType, pubsubStatusToPresence, Status, getChannelName, PRESENCE_EVENT, DEFAULT_TYPING_TIMEOUT, OnlineStatusType } from './utils';
export { createDuplexerPubSub, PubSubPresenceStatusType, Status, PRESENCE_EVENT, getChannelName, OnlineStatusType };
export * from './duplexer-pubsub/index';
export * from './testkit';
export * from './utils';
var createPresenceReporter = function (pubSub, options, authPath, tenantName) {
    var onlineTimersMap = new Map();
    var tabId = uuid();
    var isUnloadSet = false;
    var isInteracting = {};
    var interactingUser;
    var typingTimer;
    var goOfflineOnEndInteracting = false;
    var location = { url: '', title: '' };
    var isFocused = false;
    var setOnline = function (itemId, userId, window) { return __awaiter(void 0, void 0, void 0, function () {
        var getPayload, setOnlineInterval;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    getPayload = function () {
                        var isHidden = !isFocused;
                        return {
                            userId: userId,
                            status: isInteracting[itemId] ? PubSubPresenceStatusType.INTERACTING : isHidden ? PubSubPresenceStatusType.HIDDEN : PubSubPresenceStatusType.VIEWING,
                            user: interactingUser,
                            location: location,
                            tabId: tabId
                        };
                    };
                    return [4 /*yield*/, pubSub.publish(getChannelName(tenantName, itemId), PRESENCE_EVENT, getPayload(), authPath, true)];
                case 1:
                    _a.sent();
                    setOnlineInterval = function () {
                        clearTimeout(onlineTimersMap.get(itemId));
                        pubSub.publish(getChannelName(tenantName, itemId), PRESENCE_EVENT, getPayload(), authPath, true);
                        onlineTimersMap.set(itemId, setTimeout(setOnlineInterval, options.interval));
                    };
                    clearTimeout(onlineTimersMap.get(itemId));
                    onlineTimersMap.set(itemId, setTimeout(setOnlineInterval, options.interval));
                    if (!isUnloadSet) {
                        window.addEventListener('unload', function () {
                            clearTimeout(onlineTimersMap.get(itemId));
                            pubSub.publish(getChannelName(tenantName, itemId), PRESENCE_EVENT, { userId: userId, status: PubSubPresenceStatusType.OFFLINE, tabId: tabId }, authPath, true);
                        });
                        isUnloadSet = true;
                    }
                    return [2 /*return*/];
            }
        });
    }); };
    var setOffline = function (itemId, userId) {
        clearTimeout(onlineTimersMap.get(itemId));
        onlineTimersMap.delete(itemId);
        var payload = {
            userId: userId,
            status: PubSubPresenceStatusType.OFFLINE,
            tabId: tabId
        };
        pubSub.publish(getChannelName(tenantName, itemId), PRESENCE_EVENT, payload, authPath, true);
    };
    var endInteracting = function (itemId, userId) { return __awaiter(void 0, void 0, void 0, function () {
        var payload;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    clearTimeout(typingTimer);
                    isInteracting[itemId] = false;
                    interactingUser = undefined;
                    payload = {
                        userId: userId,
                        status: PubSubPresenceStatusType.VIEWING,
                        location: location,
                        tabId: tabId
                    };
                    if (!goOfflineOnEndInteracting) return [3 /*break*/, 1];
                    setOffline(itemId, userId);
                    goOfflineOnEndInteracting = false;
                    return [3 /*break*/, 3];
                case 1: return [4 /*yield*/, pubSub.publish(getChannelName(tenantName, itemId), PRESENCE_EVENT, payload, authPath, true)];
                case 2:
                    _a.sent();
                    _a.label = 3;
                case 3: return [2 /*return*/];
            }
        });
    }); };
    return {
        setOnline: setOnline,
        setOffline: setOffline,
        setInteracting: function (itemId, userId, window, user) { return __awaiter(void 0, void 0, void 0, function () {
            var payload;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        payload = {
                            userId: userId,
                            status: PubSubPresenceStatusType.INTERACTING,
                            user: user,
                            location: location,
                            tabId: tabId
                        };
                        if (!!isInteracting[itemId]) return [3 /*break*/, 3];
                        isInteracting[itemId] = true;
                        interactingUser = user;
                        if (!onlineTimersMap.get(itemId)) return [3 /*break*/, 2];
                        return [4 /*yield*/, pubSub.publish(getChannelName(tenantName, itemId), PRESENCE_EVENT, payload, authPath, true)];
                    case 1:
                        _a.sent();
                        return [3 /*break*/, 3];
                    case 2:
                        goOfflineOnEndInteracting = true;
                        setOnline(itemId, userId, window);
                        _a.label = 3;
                    case 3:
                        clearTimeout(typingTimer);
                        typingTimer = setTimeout(function () {
                            endInteracting(itemId, userId);
                        }, options.typingTimeout || DEFAULT_TYPING_TIMEOUT);
                        return [2 /*return*/];
                }
            });
        }); },
        endInteracting: endInteracting,
        setLocation: function (url, title) {
            location = { url: url, title: title };
        },
        setWindowFocus: function (focused) {
            isFocused = focused;
        }
    };
};
var isNewStatusUpgraded = function (newStatus, prevStatus) {
    // hierarchy is: offline < hidden < viewing < interacting
    if (!prevStatus) {
        return true;
    }
    if (newStatus.status === prevStatus.status) {
        return false;
    }
    switch (newStatus.status) {
        case PubSubPresenceStatusType.OFFLINE:
            return false;
        case PubSubPresenceStatusType.HIDDEN:
            return prevStatus.status === PubSubPresenceStatusType.OFFLINE;
        case PubSubPresenceStatusType.VIEWING:
            return prevStatus.status !== PubSubPresenceStatusType.INTERACTING;
        case PubSubPresenceStatusType.INTERACTING:
            return true;
    }
};
var createPresenceSubscriber = function (pubSub, options, authPath, tenantName) {
    var subscribeToStatus = function (item, cb, intialUsers) { return __awaiter(void 0, void 0, void 0, function () {
        var statusesMap, autoOfflineTimers, handler, unsubscribe, initialTimer;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    statusesMap = new Map();
                    autoOfflineTimers = new Map();
                    handler = function (pubsubStatus) {
                        clearTimeout(autoOfflineTimers.get(pubsubStatus.userId));
                        var tabId = pubsubStatus.tabId || '';
                        var itemStatuses = statusesMap.get(item);
                        var userStatus = itemStatuses && itemStatuses.get(pubsubStatus.userId);
                        var isSameTab = userStatus && (userStatus.tabId) === (pubsubStatus.tabId || '');
                        var didLocationChange = isSameTab && userStatus && (userStatus.location && pubsubStatus.location && (userStatus.location.title !== pubsubStatus.location.title || userStatus.location.url !== pubsubStatus.location.url));
                        var didSameTabStatusChange = isSameTab && userStatus && (userStatus.status !== pubsubStatus.status);
                        var didStatusUpgrade = isNewStatusUpgraded(pubsubStatus, userStatus);
                        var shouldUpdate = didSameTabStatusChange || didStatusUpgrade || didLocationChange;
                        if (shouldUpdate) {
                            var update = pubsubStatusToPresence(pubsubStatus);
                            cb(new Map([[pubsubStatus.userId, update]]));
                            statusesMap.get(item).set(pubsubStatus.userId, { status: pubsubStatus.status, tabId: tabId, location: pubsubStatus.location });
                        }
                        if (pubsubStatus.status !== PubSubPresenceStatusType.OFFLINE) {
                            autoOfflineTimers.set(pubsubStatus.userId, setTimeout(function () {
                                var offlineData = { type: Status.OFFLINE };
                                cb(new Map([[pubsubStatus.userId, offlineData]]));
                                statusesMap.get(item).set(pubsubStatus.userId, { status: PubSubPresenceStatusType.OFFLINE, tabId: tabId });
                            }, options.interval * 6));
                        }
                    };
                    statusesMap.set(item, statusesMap.get(item) || new Map());
                    return [4 /*yield*/, pubSub.subscribe(getChannelName(tenantName, item), PRESENCE_EVENT, handler, authPath, noop, true)];
                case 1:
                    unsubscribe = _a.sent();
                    initialTimer = setTimeout(function () {
                        var offlineData = { type: Status.OFFLINE };
                        var unreportedUsers = intialUsers.filter(function (user) { return !autoOfflineTimers.has(user); });
                        if (unreportedUsers.length) {
                            var itemStatuses_1 = statusesMap.get(item);
                            unreportedUsers.forEach(function (userId) { return itemStatuses_1.set(userId, { status: PubSubPresenceStatusType.OFFLINE, tabId: '' }); });
                            var offlineStatuses = unreportedUsers.map(function (userId) { return [userId, offlineData]; });
                            cb(new Map(offlineStatuses));
                        }
                    }, options.interval * 3);
                    return [2 /*return*/, function () {
                            unsubscribe();
                            clearTimeout(initialTimer);
                            autoOfflineTimers.forEach(function (timer) { return clearTimeout(timer); });
                        }];
            }
        });
    }); };
    return {
        subscribeToStatus: subscribeToStatus,
        subscribeToStatuses: function (items, cb) { return __awaiter(void 0, void 0, void 0, function () {
            var unsubscribeFunctions;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, Promise.all(items.map(function (subscribeItem) {
                            var handler = function (change) { return cb(new Map([[subscribeItem.item, change]])); };
                            return subscribeToStatus(subscribeItem.item, handler, subscribeItem.initialUsers);
                        }))];
                    case 1:
                        unsubscribeFunctions = _a.sent();
                        return [2 /*return*/, function () { return unsubscribeFunctions.forEach(function (unsubscribe) { return unsubscribe(); }); }];
                }
            });
        }); },
    };
};
export var createPresencePort = function (pubSub, options, authPath, tenantName) {
    return __assign(__assign({}, createPresenceReporter(pubSub, options, authPath, tenantName)), createPresenceSubscriber(pubSub, options, authPath, tenantName));
};
//# sourceMappingURL=index.js.map